// @ts-check
import { paintQuantumIsland } from './quantum.js';
/** @typedef {import('./terrain.js').Palette} Palette */
/** @param {CanvasRenderingContext2D} c @param {import('../game/types.js').Level} level @param {number} left @param {number} right @param {Palette} p @param {boolean} reduced */
export function paintDevices(c, level, left, right, p, reduced) {
  for (const m of level.mechanisms) {
    const d = m.definition, s = m.solid;
    if (Math.max(d.x, s.x) + d.width < left || Math.min(d.x, s.x) > right) continue;
    c.save(); c.lineWidth = 1;
    const y = -s.y - s.height;
    if (d.kind === 'spring') {
      const squash = m.warning * 5;
      c.strokeStyle = p.cyan; c.lineWidth = 2;
      c.beginPath(); c.moveTo(s.x + 8, y + s.height);
      for (let n = 0; n < 5; n++) c.lineTo(s.x + (n % 2 ? 10 : s.width - 10), y + s.height - n * (s.height - squash) / 5);
      c.stroke(); c.fillStyle = p.violet; c.fillRect(s.x, y + squash, s.width, 4);
      c.fillStyle = p.cyan; c.fillRect(s.x + 4, y + squash, s.width - 8, 2);
      if (!reduced) { c.globalAlpha = .35; c.beginPath(); c.moveTo(s.x + s.width / 2 - 5, y - 9); c.lineTo(s.x + s.width / 2, y - 15 - Math.sin(level.time * 3) * 3); c.lineTo(s.x + s.width / 2 + 5, y - 9); c.stroke(); }
    } else if (level.definition.quantum && ['carrier', 'memory', 'phase', 'gate'].includes(d.kind)) {
      c.globalAlpha = s.active ? 1 : .25;
      paintQuantumIsland(c, s, y, p);
      c.strokeStyle = p.violet; c.globalAlpha = .3;
      c.beginPath(); c.ellipse(s.x + s.width / 2, y + 16, s.width * .65, 12, 0, 0, Math.PI * 2); c.stroke();
    } else if (d.kind === 'carrier') {
      c.strokeStyle = p.gold; c.globalAlpha = 0.3;
      c.beginPath(); c.moveTo(d.x + d.width / 2, -d.y - d.height / 2); c.lineTo(d.x + d.width / 2 + (d.travelX ?? 0), -d.y - d.height / 2 - (d.travelY ?? 0)); c.stroke();
      c.globalAlpha = 1; c.fillStyle = p.panel; c.fillRect(s.x, y, s.width, s.height);
      c.strokeStyle = p.gold; c.strokeRect(s.x, y, s.width, s.height); c.fillStyle = p.gold; c.fillRect(s.x + 3, y, s.width - 6, 2);
      for (let x = 8; x < s.width - 4; x += 12) c.fillRect(s.x + x, y + 6, 5, 2);
    } else if (d.kind === 'memory' || d.kind === 'phase' || d.kind === 'gate') {
      c.globalAlpha = s.active ? 1 : 0.3; c.strokeStyle = d.kind === 'memory' ? p.violet : p.cyan;
      if (s.active) { c.fillStyle = p.panel; c.fillRect(s.x, y, s.width, s.height); }
      c.strokeRect(s.x, y, s.width, s.height);
      for (let x = 5; x < s.width - 4; x += 12) { c.beginPath(); c.moveTo(s.x + x, y + s.height - 3); c.lineTo(s.x + x + 5, y + 3); c.stroke(); }
      c.globalAlpha = 1;
      if (d.kind === 'memory') { c.fillStyle = m.warning > 0.7 ? p.danger : p.violet; c.fillRect(s.x, y - 4, s.width * (1 - m.warning), 2); }
      if (d.kind === 'gate') {
        const clock = ((level.time + (d.offset ?? 0)) % d.period) / d.period;
        for (let n = 0; n < 3; n++) { c.fillStyle = clock >= n / 3 ? p.gold : p.edge; c.fillRect(s.x + n * 6 - 1, y - 9, 4, 4); }
      }
    } else if (d.kind === 'vent') {
      c.fillStyle = p.metal; c.fillRect(d.x, -d.y - 4, d.width, 6);
      c.fillStyle = p.cyan; c.globalAlpha = m.enabled ? 0.75 : 0.2;
      for (let x = 5; x < d.width; x += 9) c.fillRect(d.x + x, -d.y - 3, 4, 2);
      const flow = reduced ? 0 : level.time * 60 % 24;
      c.strokeStyle = p.cyan;
      for (let x = 12; x < d.width; x += 18) for (let h = 12; h < d.height; h += 24) {
        c.globalAlpha = (m.enabled ? 0.42 : 0.08) * (1 - h / d.height);
        c.beginPath(); c.moveTo(d.x + x - 3, -d.y - h - flow + 4); c.lineTo(d.x + x, -d.y - h - flow); c.lineTo(d.x + x + 3, -d.y - h - flow + 4); c.stroke();
      }
    } else if (d.kind === 'scanner') {
      const stop = level.solids.filter(s => s.active && s.x > d.x && s.y <= d.y && s.y + s.height >= d.y + d.height).reduce((edge, s) => Math.min(edge, s.x), d.x + d.width);
      c.fillStyle = p.metal; c.fillRect(d.x, -d.y - d.height, 7, d.height);
      c.fillStyle = m.enabled ? p.danger : p.gold; c.fillRect(d.x + 2, -d.y - d.height + 3, 3, Math.max(2, d.height - 6));
      c.globalAlpha = m.enabled ? 0.6 : m.warning * 0.22; c.fillRect(d.x + 7, -d.y - d.height, Math.max(0, stop - d.x - 7), d.height);
      c.globalAlpha = 0.4; c.strokeStyle = p.danger; c.strokeRect(d.x, -d.y - d.height, d.width, d.height);
    } else {
      c.fillStyle = p.metal; c.fillRect(d.x, -d.y, d.width, 5); c.strokeStyle = p.gold;
      const direction = Math.sign(d.travelX ?? 1), scroll = reduced ? 0 : level.time * 30 % 12;
      for (let x = 8; x < d.width - 10; x += 14) { c.beginPath(); c.moveTo(d.x + x + scroll * direction, -d.y + 1); c.lineTo(d.x + x + scroll * direction + 4 * direction, -d.y + 3); c.lineTo(d.x + x + scroll * direction, -d.y + 5); c.stroke(); }
    }
    c.restore();
  }
  for (const checkpoint of level.definition.checkpoints ?? []) {
    if (checkpoint.x < left || checkpoint.x > right) continue;
    const active = checkpoint.id === level.checkpoint;
    c.fillStyle = p.metal; c.fillRect(checkpoint.x - 2, -42, 4, 42);
    c.strokeStyle = active ? p.cyan : p.steel; c.lineWidth = 1; c.strokeRect(checkpoint.x - 8, -48, 16, 16);
    c.fillStyle = active ? p.cyan : p.panel; c.fillRect(checkpoint.x - 5, -45, 10, 10);
  }
  for (const solid of level.solids) if (solid.signal && solid.x >= left && solid.x <= right) {
    c.strokeStyle = p.violet; c.lineWidth = 1; c.beginPath(); c.moveTo(solid.x + solid.width / 2, -solid.y); c.lineTo(solid.x + solid.width / 2, -solid.y + 10); c.lineTo(solid.x + solid.width + 18, -solid.y + 10); c.stroke();
    c.fillStyle = p.violet; c.font = '7px monospace'; c.textAlign = 'center'; c.fillText(solid.signal === 'boss' ? 'COUNTER' : 'SIGNAL', solid.x + solid.width / 2, -solid.y - solid.height - 6);
  }
}
/** Different mechanical silhouettes share a metal treatment and readable vulnerability cap.
 * @param {CanvasRenderingContext2D} c @param {import('../game/types.js').Boss} b @param {Palette} p @param {boolean} reduced
 */
export function paintBoss(c, b, p, reduced) {
  c.save();
  const { x, y, width: w, height: h } = b, kind = b.definition.kind;
  const open = b.phase === 'open', dead = b.phase === 'defeated';
  const color = dead ? p.cyan : open ? p.gold : p.danger;
  if (dead) c.globalAlpha = 0.45;
  const sy = -y - h;
  const clock = reduced ? 0 : b.cycle * 4 + b.age;
  const shell = c.createLinearGradient(x, sy, x + w, sy + h);
  shell.addColorStop(0, p.steel); shell.addColorStop(0.1, p.metal); shell.addColorStop(0.55, p.panel); shell.addColorStop(1, p.edge);
  c.fillStyle = shell; c.strokeStyle = p.steel; c.lineWidth = 1.4;
  if (kind === 'watchdog') {
    for (const dx of [2, w - 18]) { c.fillRect(x + dx, -y - 17, 16, 17); c.strokeRect(x + dx, -y - 17, 16, 17); }
    c.beginPath(); c.moveTo(x, sy + 14); c.lineTo(x + 17, sy); c.lineTo(x + w - 12, sy); c.lineTo(x + w, sy + 22); c.lineTo(x + w - 6, -y - 12); c.lineTo(x + 10, -y - 12); c.closePath(); c.fill(); c.stroke();
  } else if (kind === 'clockwarden') {
    c.fillRect(x + 16, sy, w - 32, h); c.strokeRect(x + 16, sy, w - 32, h);
    for (let i = 0; i < 3; i++) { c.fillStyle = b.age > i * 0.4 ? color : p.edge; c.fillRect(x + 26, sy + 12 + i * 15, 20, 7); }
    c.fillStyle = shell; c.fillRect(x, -y - 20, w, 20); c.strokeRect(x, -y - 20, w, 20);
  } else if (kind === 'reclaimer' || kind === 'heatsink') {
    c.fillRect(x, sy + 10, w, h - 10); c.strokeRect(x, sy + 10, w, h - 10);
    for (let n = 0; n < 8; n++) { c.fillStyle = n % 2 ? p.metal : p.steel; c.fillRect(x + 5 + n * (w - 10) / 8, sy + 4, 5, h - 8); }
    if (kind === 'reclaimer') for (let n = 0; n < 6; n++) { c.fillStyle = color; c.fillRect(x + 6 + n * 15, -y - 8, 8, 8); }
  } else if (kind === 'switchboard') {
    c.fillRect(x + 10, sy, w - 20, h); c.strokeRect(x + 10, sy, w - 20, h);
    for (let arm = 0; arm < 3; arm++) for (const side of [-1, 1]) { c.fillStyle = shell; c.fillRect(x + (side < 0 ? -18 : w - 10), sy + 8 + arm * 18, 28, 10); c.strokeRect(x + (side < 0 ? -18 : w - 10), sy + 8 + arm * 18, 28, 10); c.fillStyle = color; c.fillRect(x + (side < 0 ? -16 : w + 12), sy + 10 + arm * 18, 4, 6); }
  } else if (kind === 'raster') {
    c.fillRect(x + 8, sy, w - 16, h); c.strokeRect(x + 8, sy, w - 16, h);
    c.fillStyle = p.void; c.fillRect(x + 17, sy + 12, w - 34, h - 24); c.strokeStyle = color; c.strokeRect(x + 20, sy + 15, w - 40, h - 30);
    c.strokeStyle = p.cyan; c.beginPath(); c.moveTo(x + w / 2, -y); c.lineTo(x + w / 2, 5); c.lineTo(x - 30, 5); c.stroke();
    if (!dead) { c.globalAlpha = 0.15; c.strokeStyle = p.violet; c.strokeRect(x - 100, sy, w, h); c.strokeRect(x + 100, sy, w, h); c.globalAlpha = 1; }
  } else {
    c.beginPath(); c.moveTo(x + 12, sy); c.lineTo(x + w - 12, sy); c.lineTo(x + w, sy + 12); c.lineTo(x + w, -y - 12); c.lineTo(x + w - 12, -y); c.lineTo(x + 12, -y); c.lineTo(x, -y - 12); c.lineTo(x, sy + 12); c.closePath(); c.fill(); c.stroke();
    c.fillStyle = p.void; c.fillRect(x + 10, sy + 10, w - 20, h - 20); c.strokeStyle = color; c.strokeRect(x + 16, sy + 16, w - 32, h - 32);
    if (kind === 'null') { c.strokeStyle = p.light; c.lineWidth = 2; c.beginPath(); c.moveTo(x + 26, sy + 24); c.lineTo(x + w - 26, sy + h - 24); c.moveTo(x + w - 26, sy + 24); c.lineTo(x + 26, sy + h - 24); c.stroke(); }
  }
  // Recessed fasteners, live vents, and counter-rotating cores make the housing a machine.
  c.lineWidth = .8;
  for (const bx of [x + 5, x + w - 5]) for (const by of [sy + 15, sy + h - 8]) {
    c.fillStyle = p.void; c.beginPath(); c.arc(bx, by, 1.8, 0, Math.PI * 2); c.fill();
    c.strokeStyle = p.steel; c.beginPath(); c.moveTo(bx - .8, by); c.lineTo(bx + .8, by); c.stroke();
  }
  if (['reclaimer', 'heatsink', 'null'].includes(kind)) {
    const cx = x + w / 2, cy = sy + h / 2 + 3, radius = Math.min(w, h) * .23;
    c.fillStyle = p.void; c.strokeStyle = p.edge; c.beginPath(); c.arc(cx, cy, radius + 3, 0, Math.PI * 2); c.fill(); c.stroke();
    for (let i = 0; i < 8; i++) {
      const a = i * Math.PI / 4 + clock * (b.phase === 'attack' ? 4 : .7);
      c.strokeStyle = i % 2 ? p.steel : color;
      c.beginPath(); c.moveTo(cx + Math.cos(a) * 5, cy + Math.sin(a) * 5);
      c.lineTo(cx + Math.cos(a + .25) * radius, cy + Math.sin(a + .25) * radius); c.stroke();
    }
    c.fillStyle = color; c.beginPath(); c.arc(cx, cy, 3, 0, Math.PI * 2); c.fill();
  }
  if (kind === 'gatekeeper' || kind === 'watchdog') {
    c.fillStyle = p.void; c.fillRect(x + 18, sy + 22, w - 36, 13);
    const lens = x + w / 2 + b.side * 7;
    c.shadowColor = color; c.shadowBlur = reduced ? 0 : 8;
    c.fillStyle = color; c.fillRect(lens - 8, sy + 26, 16, 4);
    c.fillStyle = p.light; c.fillRect(lens - 4, sy + 26, 6, 1); c.shadowBlur = 0;
    for (let n = 0; n < 5; n++) { c.fillStyle = p.steel; c.fillRect(x + 19 + n * 7, sy + h - 13, 3, 5); }
  }
  if (kind === 'switchboard' || kind === 'raster') {
    for (let n = 0; n < 5; n++) { c.globalAlpha = dead ? .3 : .35 + .65 * Math.max(0, Math.sin(clock * 4 - n)); c.fillStyle = color; c.fillRect(x + 23 + n * 6, sy + h - 11, 3, 3); }
    c.globalAlpha = dead ? .45 : 1;
  }
  c.fillStyle = color; c.shadowColor = color; c.shadowBlur = reduced ? 0 : open ? 14 : 4;
  c.fillRect(x + w * 0.25, sy - 3, w * 0.5, 5); c.shadowBlur = 0;
  if (open) {
    c.globalAlpha = reduced ? .7 : .55 + Math.sin(clock * 8) * .2;
    c.strokeStyle = p.gold; c.lineWidth = 1.5;
    c.beginPath(); c.moveTo(x + w / 2 - 5, sy - 13); c.lineTo(x + w / 2, sy - 8); c.lineTo(x + w / 2 + 5, sy - 13); c.stroke(); c.globalAlpha = 1;
  }
  if (b.phase === 'tell' && ['gatekeeper', 'watchdog', 'null'].includes(kind)) {
    c.globalAlpha = 0.35; c.fillStyle = p.danger; c.fillRect(Math.min(b.target, x), -5, Math.abs(b.target - x) + w, 3);
  }
  c.restore();
  for (const a of b.attacks) {
    c.save(); const charging = a.age < a.warning;
    c.globalAlpha = charging ? 0.3 : 0.85; c.strokeStyle = charging ? p.gold : p.danger; c.fillStyle = p.danger;
    if (!charging) c.fillRect(a.x, -a.y - a.height, a.width, a.height);
    c.strokeRect(a.x, -a.y - a.height, a.width, a.height);
    if (charging) { c.fillStyle = p.gold; c.fillRect(a.x, -a.y - 2, a.width * a.age / a.warning, 2); }
    c.restore();
  }
}
